import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  ArrowLeft, FileText, Printer, Download, Plus, Trash2, Save, Eye,
  Sparkles, Clock, ChefHat, Scroll, Wallet, MessageCircle, Edit3, RotateCcw, Check
} from "lucide-react";

const TIPOS_DOC = [
  { key: "contrato",           label: "Contrato de Servicios",     icon: FileText, color: "emerald" },
  { key: "cotizacion",         label: "Cotización",                icon: Wallet,   color: "emerald" },
  { key: "solicitud_anticipo", label: "Solicitud de Anticipo",     icon: Wallet,   color: "amber" },
  { key: "itinerario",         label: "Itinerario del Evento",     icon: Clock,    color: "indigo" },
  { key: "menu",               label: "Menú del Evento",           icon: ChefHat,  color: "rose" },
  { key: "reglamento",         label: "Reglamento y Cláusulas",    icon: Scroll,   color: "stone" },
  // Iter132f · Expediente Maestro (interno/administrativo).
  { key: "expediente_maestro", label: "Expediente Maestro (interno)", icon: FileText, color: "slate" },
];

const CATEGORIAS_SUGERIDAS = [
  "Instalaciones", "Decoración", "Coffee Break",
  "Desayuno", "Comida", "Cena", "Bebidas", "Pastel",
  "Fotografía", "Animación", "Música/DJ", "Kids Club",
  "Transporte", "Montaje", "Coordinación", "Otro"
];

const DocumentosEvento = () => {
  const { eventoId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [previewTipo, setPreviewTipo] = useState(null); // key del tipo abierto
  const [previewHtml, setPreviewHtml] = useState("");
  const [precargado, setPrecargado] = useState({});
  const [empresaBank, setEmpresaBank] = useState({ banco: "", clabe: "", titular: "", whatsapp: "" });
  const [docsCustom, setDocsCustom] = useState([]);
  const [docCustomForm, setDocCustomForm] = useState({ open: false, doc_id: null, titulo: "", contenido: "", tipo: "personalizado" });
  // Iter152 · Editor visual de "copia editable" por tipo canónico (contrato, etc.)
  const [editorTipo, setEditorTipo] = useState(null);
  const [editorHtml, setEditorHtml] = useState("");
  const [editorSaving, setEditorSaving] = useState(false);
  const TIPOS_EDITABLES = new Set(["contrato", "cotizacion", "itinerario", "reglamento", "menu", "expediente_maestro"]);
  const [catalogoOpen, setCatalogoOpen] = useState(false);
  const [catalogoItems, setCatalogoItems] = useState([]);
  const [catalogoSel, setCatalogoSel] = useState(new Set());
  const [catalogoSearch, setCatalogoSearch] = useState("");
  const [datos, setDatos] = useState({
    cliente: { nombre: "", whatsapp: "", email: "" },
    evento: {
      evento_id: "", tipo: "", nombre: "", tematica: "", nombre_festejado: "",
      fecha: "", hora_inicio: "", hora_fin: "",
      adultos: 0, ninos: 0,
      unidad_negocio: "", total: 0, anticipo: 0,
      servicios_incluidos: [],
      personal_incluido: [],
      itinerario: [],
      menu_desglose: { momento: "Desayuno", coffee_break: [], platillos: [], bebidas: [] },
      clausulas: [],
      datos_bancarios: { banco: "", clabe: "", titular: "", whatsapp: "" },
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/eventos/${eventoId}/documento-datos`);
      // Normalizar menu_desglose para que siempre tenga la estructura
      const mnu = data.evento.menu_desglose || {};
      data.evento.menu_desglose = {
        momento: mnu.momento || "Desayuno",
        coffee_break: mnu.coffee_break || [],
        platillos: mnu.platillos || [],
        bebidas: mnu.bebidas || [],
      };
      data.evento.datos_bancarios = data.evento.datos_bancarios || { banco: "", clabe: "", titular: "", whatsapp: "" };
      setDatos(data);
      setPrecargado(data.precargado || {});
    } catch (err) {
      toast.error("No se pudo cargar el evento");
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Cargar datos bancarios a nivel empresa (reutilizables)
  useEffect(() => {
    axios.get(`${API}/empresa/datos-bancarios`)
      .then(r => setEmpresaBank({ banco: "", clabe: "", titular: "", whatsapp: "", ...(r.data || {}) }))
      .catch(() => { /* no-op */ });
  }, []);

  // Cargar documentos custom del evento
  useEffect(() => {
    axios.get(`${API}/eventos/${eventoId}/documentos-custom`)
      .then(r => setDocsCustom(r.data || []))
      .catch(() => setDocsCustom([]));
  }, [eventoId]);

  const abrirNuevoDocCustom = () => setDocCustomForm({ open: true, doc_id: null, titulo: "", contenido: "", tipo: "personalizado" });
  const abrirEditarDocCustom = (d) => setDocCustomForm({ open: true, doc_id: d.doc_id, titulo: d.titulo, contenido: d.contenido || "", tipo: d.tipo || "personalizado" });
  const guardarDocCustom = async () => {
    if (!docCustomForm.titulo.trim()) { toast.error("Título requerido"); return; }
    try {
      if (docCustomForm.doc_id) {
        await axios.put(`${API}/eventos/${eventoId}/documentos-custom/${docCustomForm.doc_id}`, {
          titulo: docCustomForm.titulo, contenido: docCustomForm.contenido, tipo: docCustomForm.tipo,
        });
        toast.success("Documento actualizado");
      } else {
        await axios.post(`${API}/eventos/${eventoId}/documentos-custom`, {
          titulo: docCustomForm.titulo, contenido: docCustomForm.contenido, tipo: docCustomForm.tipo,
        });
        toast.success("Documento creado");
      }
      const r = await axios.get(`${API}/eventos/${eventoId}/documentos-custom`);
      setDocsCustom(r.data || []);
      setDocCustomForm({ open: false, doc_id: null, titulo: "", contenido: "", tipo: "personalizado" });
    } catch { toast.error("Error al guardar"); }
  };
  const eliminarDocCustom = async (id) => {
    if (!window.confirm("¿Eliminar este documento personalizado?")) return;
    try {
      await axios.delete(`${API}/eventos/${eventoId}/documentos-custom/${id}`);
      setDocsCustom(prev => prev.filter(d => d.doc_id !== id));
      toast.success("Eliminado");
    } catch { toast.error("Error"); }
  };

  const guardarDatosEmpresa = async () => {
    setSavingBank(true);
    try {
      await axios.post(`${API}/empresa/datos-bancarios`, empresaBank);
      toast.success("Datos bancarios de la empresa guardados. Se aplicarán a futuros eventos.");
      // Aplicar también al evento actual si está vacío
      if (!datos.evento.datos_bancarios?.clabe) {
        updEvento({ datos_bancarios: { ...empresaBank } });
      }
    } catch {
      toast.error("Error al guardar datos bancarios de la empresa");
    } finally {
      setSavingBank(false);
    }
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const e = datos.evento;
      await axios.post(`${API}/eventos/${eventoId}/documento-datos`, {
        tematica: e.tematica,
        nombre_festejado: e.nombre_festejado,
        titulo_contrato: e.titulo_contrato,
        subtitulo_contrato: e.subtitulo_contrato,
        servicios_incluidos: e.servicios_incluidos,
        personal_incluido: e.personal_incluido,
        itinerario: e.itinerario,
        menu_desglose: e.menu_desglose,
        clausulas: e.clausulas,
        datos_bancarios: e.datos_bancarios,
        total: Number(e.total) || 0,
        anticipo: Number(e.anticipo) || 0,
      });
      toast.success("Datos guardados. Los servicios y personal también se sincronizaron al expediente.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const previewDoc = async (tipo) => {
    setPreviewTipo(tipo);
    setPreviewHtml("");
    try {
      const url = `${BACKEND_URL}/api/eventos/${eventoId}/documento-html?tipo=${tipo}`;
      const res = await axios.get(url, { responseType: "text" });
      setPreviewHtml(res.data);
    } catch {
      toast.error("Error al cargar vista previa");
    }
  };

  const abrirEnNuevaVentana = async (tipo) => {
    // Iter151 · No usar window.open directo (pierde la sesión en producción / cross-domain).
    // Descarga el HTML autenticado con axios y lo abre como Blob URL.
    try {
      const url = `${BACKEND_URL}/api/eventos/${eventoId}/documento-html?tipo=${tipo}`;
      const res = await axios.get(url, { responseType: "text" });
      const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      toast.error("Error al abrir el documento");
    }
  };

  const descargarPDF = async (tipo) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/eventos/${eventoId}/documento-pdf?tipo=${tipo}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tipo}_${eventoId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch {
      toast.error("Error al generar PDF. Intenta con 'Ver' e imprime desde el navegador.");
    }
  };

  const enviarWhatsApp = async (tipo) => {
    try {
      toast.info("Generando PDF y link para WhatsApp...");
      const { data } = await axios.post(`${BACKEND_URL}/api/eventos/${eventoId}/documento-link-whatsapp`, { tipo });
      // Prefijar URL pública con BACKEND_URL si viene relativa
      const urlPub = data.url_publica.startsWith("http") ? data.url_publica : `${BACKEND_URL}${data.url_publica}`;
      // Reconstruir mensaje con URL absoluta
      const tel = data.telefono_cliente || "";
      const mensaje = data.mensaje.replace(data.url_publica, urlPub);
      const waUrl = `https://wa.me/${tel}?text=${encodeURIComponent(mensaje)}`;
      window.open(waUrl, "_blank");
      // Copiar link al portapapeles como respaldo
      try {
        await navigator.clipboard.writeText(urlPub);
        toast.success("Link del PDF copiado al portapapeles");
      } catch { /* noop */ }
    } catch {
      toast.error("No se pudo generar el link");
    }
  };

  const imprimirDoc = () => {
    // Abre el iframe de la preview para imprimir
    const iframe = document.getElementById("doc-preview-iframe");
    if (!iframe) return;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      toast.error("No se pudo imprimir. Abre en pestaña nueva.");
    }
  };

  // ═══ Iter152 · EDITOR VISUAL de copia editable (por tipo canónico) ═══
  const docCustomPorTipo = (tipo) => (docsCustom || []).find(d => (d.tipo || "").toLowerCase() === tipo);

  const abrirEditor = async (tipo) => {
    try {
      // Carga siempre el HTML actual servido por el backend (que ya devuelve
      // la copia editada si existe, o el render vivo si no).
      const { data } = await axios.get(`${BACKEND_URL}/api/eventos/${eventoId}/documento-html?tipo=${tipo}`, { responseType: "text" });
      setEditorHtml(data);
      setEditorTipo(tipo);
    } catch {
      toast.error("No se pudo abrir el editor");
    }
  };

  const guardarEditor = async () => {
    if (!editorTipo) return;
    setEditorSaving(true);
    try {
      // Extraer el HTML editado desde el iframe (documento completo)
      const iframe = document.getElementById("doc-editor-iframe");
      const html = iframe?.contentDocument?.documentElement?.outerHTML || editorHtml;
      const existente = docCustomPorTipo(editorTipo);
      const payload = {
        tipo: editorTipo,
        titulo: TIPOS_DOC.find(t => t.key === editorTipo)?.label || editorTipo,
        contenido: `<!DOCTYPE html>\n${html}`,
      };
      if (existente) {
        await axios.put(`${API}/eventos/${eventoId}/documentos-custom/${existente.doc_id}`, payload);
      } else {
        await axios.post(`${API}/eventos/${eventoId}/documentos-custom`, payload);
      }
      toast.success("Copia editable guardada");
      const { data } = await axios.get(`${API}/eventos/${eventoId}/documentos-custom`);
      setDocsCustom(data || []);
      setEditorTipo(null);
      setEditorHtml("");
    } catch {
      toast.error("No se pudo guardar la copia");
    } finally {
      setEditorSaving(false);
    }
  };

  const restaurarDocumento = async (tipo) => {
    const existente = docCustomPorTipo(tipo);
    if (!existente) return;
    if (!window.confirm("¿Restaurar a los datos actuales del evento? Se eliminará la copia editada.")) return;
    try {
      await axios.delete(`${API}/eventos/${eventoId}/documentos-custom/${existente.doc_id}`);
      toast.success("Restaurado a datos actuales del evento");
      const { data } = await axios.get(`${API}/eventos/${eventoId}/documentos-custom`);
      setDocsCustom(data || []);
    } catch {
      toast.error("No se pudo restaurar");
    }
  };

  // Cuando el iframe carga, activamos contentEditable en el body
  const activarEdicionVisual = (ev) => {
    try {
      const doc = ev.currentTarget.contentDocument;
      if (doc && doc.body) {
        doc.body.setAttribute("contenteditable", "true");
        doc.body.style.outline = "none";
        doc.body.style.caretColor = "#4a5f3e";
      }
    } catch { /* noop */ }
  };

  // ── Helpers para arrays anidados ──
  const updEvento = (patch) => setDatos(d => ({ ...d, evento: { ...d.evento, ...patch } }));
  const setSrv = (i, patch) => {
    const arr = [...datos.evento.servicios_incluidos];
    arr[i] = { ...arr[i], ...patch };
    updEvento({ servicios_incluidos: arr });
  };
  const addSrv = () => updEvento({ servicios_incluidos: [...datos.evento.servicios_incluidos, { categoria: "Instalaciones", nombre: "" }] });
  const delSrv = (i) => updEvento({ servicios_incluidos: datos.evento.servicios_incluidos.filter((_, x) => x !== i) });

  // Picker de catálogo
  const abrirCatalogo = async () => {
    setCatalogoOpen(true);
    setCatalogoSel(new Set());
    try {
      const { data } = await axios.get(`${API}/catalogo`);
      setCatalogoItems((data || []).filter(x => x.activo !== false));
    } catch {
      toast.error("No se pudo cargar el catálogo");
    }
  };
  const toggleCatalogoItem = (id) => {
    setCatalogoSel(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const importarSeleccionados = () => {
    const seleccionados = catalogoItems.filter(it => catalogoSel.has(it.item_id));
    if (seleccionados.length === 0) { toast.error("Selecciona al menos un item"); return; }
    const nuevos = [];
    for (const it of seleccionados) {
      // Regla de negocio: si es paquete con sub-servicios, expandir cada uno
      if (it.es_paquete_completo && Array.isArray(it.sub_servicios) && it.sub_servicios.length > 0) {
        for (const sub of it.sub_servicios) {
          nuevos.push({
            categoria: sub.categoria || it.categoria || "Otro",
            nombre: sub.nombre || "",
            precio: Number(sub.precio) || 0,
            catalogo_item_id: it.item_id,
            paquete_origen: it.nombre,
          });
        }
      } else {
        nuevos.push({
          categoria: it.categoria || "Otro",
          nombre: it.nombre,
          precio: Number(it.precio) || 0,
          catalogo_item_id: it.item_id,
        });
      }
    }
    updEvento({ servicios_incluidos: [...(datos.evento.servicios_incluidos || []), ...nuevos] });
    const totPaq = seleccionados.filter(x => x.es_paquete_completo && (x.sub_servicios || []).length > 0).length;
    toast.success(`${nuevos.length} items agregados${totPaq ? ` (${totPaq} paquete${totPaq > 1 ? 's' : ''} expandido${totPaq > 1 ? 's' : ''})` : ""}`);
    setCatalogoOpen(false);
  };

  // ═══ BIBLIOTECA DE PLANTILLAS ═══
  const [plantillasOpen, setPlantillasOpen] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [guardarPlantillaForm, setGuardarPlantillaForm] = useState({ open: false, nombre: "" });

  const abrirPlantillas = async () => {
    setPlantillasOpen(true);
    try {
      const { data } = await axios.get(`${API}/plantillas-evento`);
      setPlantillas(data || []);
    } catch { toast.error("Error al cargar plantillas"); }
  };
  const aplicarPlantilla = async (plantillaId, mode = "agregar") => {
    try {
      await axios.post(`${API}/plantillas-evento/${plantillaId}/aplicar-a-evento/${eventoId}`, { mode });
      toast.success(`Plantilla aplicada (${mode === 'agregar' ? 'agregada' : 'reemplaza los campos'}). Recargando…`);
      setPlantillasOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al aplicar plantilla");
    }
  };
  const guardarComoPlantilla = async () => {
    const nombre = guardarPlantillaForm.nombre.trim();
    if (!nombre) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/plantillas-evento/desde-evento/${eventoId}`, { nombre });
      toast.success("Plantilla guardada en tu biblioteca");
      setGuardarPlantillaForm({ open: false, nombre: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar plantilla");
    }
  };
  const eliminarPlantilla = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/plantillas-evento/${id}`);
      setPlantillas(prev => prev.filter(p => p.plantilla_id !== id));
      toast.success("Plantilla eliminada");
    } catch { toast.error("Error"); }
  };

  const setItin = (i, patch) => {
    const arr = [...datos.evento.itinerario];
    arr[i] = { ...arr[i], ...patch };
    updEvento({ itinerario: arr });
  };
  const addItin = () => updEvento({ itinerario: [...datos.evento.itinerario, { hora: "", titulo: "", detalles: [] }] });
  const delItin = (i) => updEvento({ itinerario: datos.evento.itinerario.filter((_, x) => x !== i) });

  const setMenu = (patch) => updEvento({ menu_desglose: { ...datos.evento.menu_desglose, ...patch } });

  // Cláusulas del contrato
  const setClausula = (i, val) => {
    const arr = [...(datos.evento.clausulas || [])];
    arr[i] = val;
    updEvento({ clausulas: arr });
  };
  const addClausula = () => updEvento({ clausulas: [...(datos.evento.clausulas || []), ""] });
  const delClausula = (i) => updEvento({ clausulas: (datos.evento.clausulas || []).filter((_, x) => x !== i) });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const e = datos.evento;

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/panel/eventos/${eventoId}`} className="p-2 hover:bg-stone-100 rounded" data-testid="btn-back-evento">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl text-stone-900">Documentos del Evento</h1>
            <p className="text-sm text-stone-600 mt-0.5">{e.nombre || e.tipo || "Evento"}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={abrirPlantillas} className="border-purple-300 text-purple-700" data-testid="btn-abrir-plantillas">
            <Sparkles className="w-4 h-4 mr-1" /> Plantillas
          </Button>
          <Button variant="outline" onClick={() => setGuardarPlantillaForm({ open: true, nombre: "" })} className="border-purple-300 text-purple-700" data-testid="btn-guardar-plantilla">
            <Save className="w-4 h-4 mr-1" /> Guardar como plantilla
          </Button>
          <Button onClick={guardar} disabled={saving} className="bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-guardar-documentos">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar datos"}
          </Button>
        </div>
      </div>

      {/* Chips de precarga automática */}
      {(precargado.servicios_precargados || precargado.personal_precargado || precargado.menu_precargado || precargado.bancarios_precargados || precargado.num_pagos > 0) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="precarga-info">
          <p className="text-sm font-semibold text-emerald-900 mb-2">✨ Datos precargados automáticamente desde el evento</p>
          <div className="flex flex-wrap gap-2">
            {precargado.servicios_precargados && <span className="text-xs px-2 py-1 bg-white border border-emerald-300 rounded-full">📋 Servicios desde el expediente</span>}
            {precargado.personal_precargado && <span className="text-xs px-2 py-1 bg-white border border-emerald-300 rounded-full">👥 Personal asignado</span>}
            {precargado.menu_precargado && <span className="text-xs px-2 py-1 bg-white border border-emerald-300 rounded-full">🍽 Menú asignado</span>}
            {precargado.bancarios_precargados && <span className="text-xs px-2 py-1 bg-white border border-emerald-300 rounded-full">🏦 Bancarios desde la empresa</span>}
            {precargado.num_pagos > 0 && <span className="text-xs px-2 py-1 bg-white border border-amber-400 rounded-full text-amber-800">💰 Anticipo real: ${(precargado.anticipo_real_desde_pagos || 0).toLocaleString("es-MX")} ({precargado.num_pagos} pagos)</span>}
          </div>
          <p className="text-xs text-emerald-800 mt-2">Puedes editar cualquier campo. Guarda al terminar para que quede fijo en este evento.</p>
        </div>
      )}

      {/* Grid de documentos */}
      <div>
        <h2 className="font-display text-lg text-stone-800 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-700" />
          Documentos disponibles
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {TIPOS_DOC.map(({ key, label, icon: Icon }) => {
            const editable = TIPOS_EDITABLES.has(key);
            const editado = editable && !!docCustomPorTipo(key);
            return (
            <Card key={key} className="border-stone-200 hover:shadow-md transition-shadow relative" data-testid={`doc-card-${key}`}>
              {editado && (
                <span
                  className="absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
                  style={{ background: "#eef1e7", color: "#4a5f3e", border: "1px solid #cfd7bd" }}
                  data-testid={`chip-editado-${key}`}
                >
                  <Check className="w-3 h-3" /> Editado
                </span>
              )}
              <CardContent className="p-4 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-2">
                  <Icon className="w-6 h-6 text-emerald-700" />
                </div>
                <p className="text-sm font-medium text-stone-800 mb-3 leading-tight">{label}</p>
                <div className="grid grid-cols-3 gap-1 w-full">
                  <Button size="sm" variant="outline" onClick={() => previewDoc(key)} className="text-xs px-1" data-testid={`btn-preview-${key}`} title="Ver">
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => descargarPDF(key)} className="text-xs px-1 text-emerald-700 border-emerald-300" data-testid={`btn-pdf-${key}`} title="Descargar PDF">
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => enviarWhatsApp(key)} className="text-xs px-1 text-green-700 border-green-300" data-testid={`btn-wa-${key}`} title="Enviar por WhatsApp">
                    <MessageCircle className="w-3 h-3" />
                  </Button>
                </div>
                {editable && (
                  <div className={`grid ${editado ? "grid-cols-2" : "grid-cols-1"} gap-1 w-full mt-1`}>
                    <Button size="sm" variant="outline" onClick={() => abrirEditor(key)} className="text-xs px-1 border-amber-300 text-amber-800" data-testid={`btn-editar-${key}`} title={editado ? "Continuar editando la copia" : "Editar copia"}>
                      <Edit3 className="w-3 h-3 mr-1" />{editado ? "Editar copia" : "Editar copia"}
                    </Button>
                    {editado && (
                      <Button size="sm" variant="outline" onClick={() => restaurarDocumento(key)} className="text-xs px-1 border-stone-300 text-stone-700" data-testid={`btn-restaurar-${key}`} title="Restaurar a datos actuales del evento">
                        <RotateCcw className="w-3 h-3 mr-1" />Restaurar
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>

      {/* Documentos personalizados del usuario */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="font-display text-lg text-stone-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-700" />
            Mis documentos personalizados
          </h2>
          <Button size="sm" onClick={abrirNuevoDocCustom} className="bg-purple-800 hover:bg-purple-900 text-white" data-testid="btn-nuevo-doc-custom">
            <Plus className="w-4 h-4 mr-1" /> Nuevo documento
          </Button>
        </div>
        {docsCustom.length === 0 ? (
          <Card className="border-dashed border-2 border-stone-200">
            <CardContent className="p-8 text-center text-stone-500">
              <FileText className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin documentos personalizados aún.</p>
              <p className="text-xs mt-1">Crea listas de pendientes, cartas, checklist, briefings o lo que necesites.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {docsCustom.map(d => (
              <Card key={d.doc_id} className="border-purple-200 hover:shadow-md" data-testid={`doc-custom-${d.doc_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-medium text-stone-800 flex-1 truncate">{d.titulo}</h4>
                    <div className="flex gap-1">
                      <button onClick={() => abrirEditarDocCustom(d)} className="text-emerald-700 hover:bg-emerald-50 p-1 rounded" title="Editar" data-testid={`btn-edit-doc-${d.doc_id}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => eliminarDocCustom(d.doc_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Eliminar" data-testid={`btn-del-doc-${d.doc_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 line-clamp-3 whitespace-pre-line">{d.contenido || "Sin contenido"}</p>
                  <p className="text-[10px] text-stone-400 mt-2">{d.tipo}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal picker de Catálogo */}
      <Dialog open={catalogoOpen} onOpenChange={setCatalogoOpen}>
        <DialogContent className="sm:max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="dialog-catalogo-picker">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200 flex-shrink-0">
            <DialogTitle className="font-display text-xl">Agregar del catálogo</DialogTitle>
            <p className="text-xs text-stone-500">Selecciona los paquetes/productos que quieres incluir en este evento. Sus precios se copian automáticamente.</p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-5">
            <Input
              value={catalogoSearch}
              onChange={(e) => setCatalogoSearch(e.target.value)}
              placeholder="Buscar en catálogo…"
              className="mb-3"
              data-testid="catalogo-picker-search"
            />
            {catalogoItems.length === 0 ? (
              <div className="text-center py-8 text-stone-500 text-sm">
                <FileText className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                <p>Tu catálogo está vacío. Crea items en <a href="/panel/catalogo" className="text-emerald-700 underline">Catálogo</a>.</p>
              </div>
            ) : (
              <div className="space-y-1">
                {catalogoItems
                  .filter(it => !catalogoSearch || (it.nombre + " " + (it.categoria || "") + " " + (it.descripcion || "")).toLowerCase().includes(catalogoSearch.toLowerCase()))
                  .map(it => {
                    const activo = catalogoSel.has(it.item_id);
                    return (
                      <label key={it.item_id} className={`flex items-center gap-3 p-2 rounded border cursor-pointer transition ${activo ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"}`} data-testid={`cat-item-${it.item_id}`}>
                        <input type="checkbox" checked={activo} onChange={() => toggleCatalogoItem(it.item_id)} className="w-4 h-4 accent-emerald-700" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-stone-800">{it.nombre}</span>
                            {it.categoria && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded">{it.categoria}</span>}
                          </div>
                          {it.descripcion && <p className="text-xs text-stone-500 truncate">{it.descripcion}</p>}
                        </div>
                        <span className="font-medium text-emerald-800 text-sm whitespace-nowrap">${Number(it.precio || 0).toLocaleString("es-MX")}</span>
                      </label>
                    );
                  })}
              </div>
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-white flex-shrink-0 gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={() => setCatalogoOpen(false)} className="w-full sm:w-auto">Cancelar</Button>
            <Button onClick={importarSeleccionados} disabled={catalogoSel.size === 0} className="bg-emerald-900 hover:bg-emerald-800 text-white w-full sm:w-auto" data-testid="btn-importar-catalogo">
              Agregar {catalogoSel.size > 0 ? `(${catalogoSel.size})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal biblioteca de plantillas */}
      <Dialog open={plantillasOpen} onOpenChange={setPlantillasOpen}>
        <DialogContent className="sm:max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="dialog-plantillas">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200 flex-shrink-0">
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-700" /> Biblioteca de plantillas</DialogTitle>
            <p className="text-xs text-stone-500">Aplicar una plantilla copia sus servicios, personal, itinerario, menú y cláusulas a este evento.</p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-5">
            {plantillas.length === 0 ? (
              <div className="text-center py-10 text-stone-500">
                <Sparkles className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Sin plantillas todavía.</p>
                <p className="text-xs mt-1">Guarda este evento como plantilla para reutilizarlo en el futuro.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {plantillas.map(p => (
                  <div key={p.plantilla_id} className="border border-stone-200 rounded-lg p-3" data-testid={`plantilla-${p.plantilla_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800">{p.nombre}</p>
                        {p.tipo_evento && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">{p.tipo_evento}</span>}
                        {p.descripcion && <p className="text-xs text-stone-500 mt-1">{p.descripcion}</p>}
                        <p className="text-[10px] text-stone-400 mt-1">
                          {(p.servicios_incluidos || []).length} servicios · {(p.personal_incluido || []).length} roles · {(p.itinerario || []).length} bloques itinerario
                        </p>
                      </div>
                      <button onClick={() => eliminarPlantilla(p.plantilla_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`del-plt-${p.plantilla_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => aplicarPlantilla(p.plantilla_id, "agregar")} className="text-xs" data-testid={`aplicar-plt-${p.plantilla_id}`}>
                        Agregar al evento
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => aplicarPlantilla(p.plantilla_id, "reemplazar")} className="text-xs text-amber-700 border-amber-300" data-testid={`reemplazar-plt-${p.plantilla_id}`}>
                        Reemplazar campos vacíos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-white flex-shrink-0">
            <Button variant="outline" onClick={() => setPlantillasOpen(false)} className="w-full sm:w-auto">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal guardar como plantilla */}
      {guardarPlantillaForm.open && (
        <Dialog open={guardarPlantillaForm.open} onOpenChange={(o) => setGuardarPlantillaForm(f => ({ ...f, open: o }))}>
          <DialogContent className="sm:max-w-md w-[calc(100vw-1rem)]" data-testid="dialog-guardar-plantilla">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Guardar como plantilla</DialogTitle>
              <p className="text-xs text-stone-500">Guardarás todos los servicios, personal, itinerario, menú y cláusulas de este evento como plantilla reutilizable.</p>
            </DialogHeader>
            <div className="space-y-3 py-3">
              <div>
                <Label>Nombre de la plantilla *</Label>
                <Input value={guardarPlantillaForm.nombre} onChange={(ev) => setGuardarPlantillaForm(f => ({ ...f, nombre: ev.target.value }))} placeholder="Ej. Baby Shower Completo 30 pers." autoFocus data-testid="input-nombre-plantilla" />
              </div>
            </div>
            <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
              <Button variant="outline" onClick={() => setGuardarPlantillaForm({ open: false, nombre: "" })}>Cancelar</Button>
              <Button onClick={guardarComoPlantilla} className="bg-purple-800 text-white" data-testid="btn-confirmar-guardar-plantilla">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal editor de doc custom */}
      {docCustomForm.open && (
        <Dialog open={docCustomForm.open} onOpenChange={(o) => setDocCustomForm(f => ({ ...f, open: o }))}>
          <DialogContent className="sm:max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="dialog-doc-custom">
            <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200 flex-shrink-0">
              <DialogTitle className="font-display text-xl">{docCustomForm.doc_id ? "Editar" : "Nuevo"} documento personalizado</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4 px-5 flex-1 overflow-y-auto">
              <div>
                <Label>Título *</Label>
                <Input value={docCustomForm.titulo} onChange={(ev) => setDocCustomForm(f => ({ ...f, titulo: ev.target.value }))} placeholder="Lista de pendientes · Briefing cliente · Checklist montaje…" data-testid="input-doc-titulo" />
              </div>
              <div>
                <Label>Tipo (etiqueta libre)</Label>
                <Input value={docCustomForm.tipo} onChange={(ev) => setDocCustomForm(f => ({ ...f, tipo: ev.target.value }))} placeholder="checklist · briefing · carta · nota…" data-testid="input-doc-tipo" />
              </div>
              <div>
                <Label>Contenido</Label>
                <Textarea value={docCustomForm.contenido} onChange={(ev) => setDocCustomForm(f => ({ ...f, contenido: ev.target.value }))} rows={12} placeholder="Escribe tu documento libre aquí…" data-testid="input-doc-contenido" />
              </div>
            </div>
            <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-white flex-shrink-0 gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
              <Button variant="outline" onClick={() => setDocCustomForm(f => ({ ...f, open: false }))} className="w-full sm:w-auto">Cancelar</Button>
              <Button onClick={guardarDocCustom} className="bg-purple-800 hover:bg-purple-900 text-white w-full sm:w-auto" data-testid="btn-guardar-doc-custom">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Formulario de datos */}
      <div className="space-y-4">
        <h2 className="font-display text-lg text-stone-800 flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-700" />
          Datos utilizados en los documentos
        </h2>

        {/* Datos generales */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-stone-700">Personalización del evento</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Temática (ej. Winnie Pooh, Frozen…)</Label>
                <Input value={e.tematica || ""} onChange={(ev) => updEvento({ tematica: ev.target.value })} placeholder="Winnie Pooh" data-testid="input-tematica" />
              </div>
              <div>
                <Label>Nombre del festejado / bebé</Label>
                <Input value={e.nombre_festejado || ""} onChange={(ev) => updEvento({ nombre_festejado: ev.target.value })} placeholder="Rodrigo" data-testid="input-festejado" />
              </div>
              <div>
                <Label>Costo total (MXN)</Label>
                <Input type="number" value={e.total || 0} onChange={(ev) => updEvento({ total: ev.target.value })} data-testid="input-total" />
              </div>
              <div>
                <Label>Anticipo recibido (MXN)</Label>
                <Input type="number" value={e.anticipo || 0} onChange={(ev) => updEvento({ anticipo: ev.target.value })} data-testid="input-anticipo" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Título del contrato (editable) */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-stone-700">Título del contrato (editable)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Título principal</Label>
                <Input value={e.titulo_contrato || ""} onChange={(ev) => updEvento({ titulo_contrato: ev.target.value })} placeholder="CONTRATO" data-testid="input-titulo-contrato" />
                <p className="text-[10px] text-stone-500 mt-1">Deja vacío para usar &quot;CONTRATO&quot; por defecto</p>
              </div>
              <div>
                <Label>Subtítulo</Label>
                <Input value={e.subtitulo_contrato || ""} onChange={(ev) => updEvento({ subtitulo_contrato: ev.target.value })} placeholder="DE PRESTACIÓN DE SERVICIOS" data-testid="input-subtitulo-contrato" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Servicios incluidos */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm font-semibold text-stone-700">Servicios incluidos (aparecen agrupados en el contrato)</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={abrirCatalogo} className="border-emerald-300 text-emerald-800" data-testid="btn-picker-catalogo"><Sparkles className="w-3 h-3 mr-1" />Del catálogo</Button>
                <Button size="sm" variant="outline" onClick={addSrv} data-testid="btn-add-servicio"><Plus className="w-3 h-3 mr-1" />Agregar manual</Button>
              </div>
            </div>
            {e.servicios_incluidos.length === 0 && <p className="text-sm text-stone-500 italic">Sin servicios. Agrega uno.</p>}
            <div className="space-y-2">
              {e.servicios_incluidos.map((s, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`row-srv-${i}`}>
                  <Input
                    list="categorias-servicio-list"
                    value={s.categoria || ""}
                    onChange={(ev) => setSrv(i, { categoria: ev.target.value })}
                    placeholder="Categoría"
                    className="w-40"
                    data-testid={`inp-srv-cat-${i}`}
                  />
                  <Input value={s.nombre || ""} onChange={(ev) => setSrv(i, { nombre: ev.target.value })} placeholder="Nombre del servicio (libre)" className="flex-1" data-testid={`input-srv-nom-${i}`} />
                  <button onClick={() => delSrv(i)} className="text-red-500 hover:bg-red-50 p-2 rounded" data-testid={`btn-del-srv-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <datalist id="categorias-servicio-list">
                {CATEGORIAS_SUGERIDAS.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </CardContent>
        </Card>

        {/* Personal */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-semibold text-stone-700">Personal incluido (separa con coma)</p>
            <Input
              value={(e.personal_incluido || []).join(", ")}
              onChange={(ev) => updEvento({ personal_incluido: ev.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
              placeholder="Personal de cocina, Meseros, Coordinación del evento, Personal de limpieza"
              data-testid="input-personal"
            />
          </CardContent>
        </Card>

        {/* Itinerario */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-stone-700">Itinerario</p>
              <Button size="sm" variant="outline" onClick={addItin} data-testid="btn-add-itin"><Plus className="w-3 h-3 mr-1" />Agregar bloque</Button>
            </div>
            {e.itinerario.length === 0 && <p className="text-sm text-stone-500 italic">Sin bloques. Agrega uno.</p>}
            <div className="space-y-3">
              {e.itinerario.map((b, i) => (
                <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid={`row-itin-${i}`}>
                  <div className="flex gap-2">
                    <Input value={b.hora || ""} onChange={(ev) => setItin(i, { hora: ev.target.value })} placeholder="9:30 – 10:00 a.m." className="w-48" data-testid={`input-itin-hora-${i}`} />
                    <Input value={b.titulo || ""} onChange={(ev) => setItin(i, { titulo: ev.target.value })} placeholder="Recepción de invitados" className="flex-1" data-testid={`input-itin-titulo-${i}`} />
                    <button onClick={() => delItin(i)} className="text-red-500 hover:bg-red-50 p-2 rounded" data-testid={`btn-del-itin-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <Textarea
                    value={(b.detalles || []).join("\n")}
                    onChange={(ev) => setItin(i, { detalles: ev.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                    placeholder="Sub-actividades (una por línea)"
                    rows={2}
                    className="text-sm"
                    data-testid={`ta-itin-det-${i}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cláusulas del contrato */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-stone-700">Cláusulas del contrato</p>
                <p className="text-xs text-stone-500">Si dejas vacío, se usarán las 6 cláusulas por defecto.</p>
              </div>
              <Button size="sm" variant="outline" onClick={addClausula} data-testid="btn-add-clausula"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
            </div>
            {(e.clausulas || []).length === 0 && <p className="text-xs text-stone-500 italic">Sin cláusulas personalizadas — se usarán las estándar del sistema.</p>}
            <div className="space-y-2">
              {(e.clausulas || []).map((c, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`row-clausula-${i}`}>
                  <span className="text-xs font-medium text-stone-500 pt-2 w-8">{i + 1}.</span>
                  <Textarea value={c} onChange={(ev) => setClausula(i, ev.target.value)} rows={2} className="flex-1 text-sm" placeholder="Texto de la cláusula…" data-testid={`inp-clausula-${i}`} />
                  <button onClick={() => delClausula(i)} className="text-red-500 hover:bg-red-50 p-2 rounded" data-testid={`btn-del-clausula-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Menú desglose */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-stone-700">Menú del evento</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Momento</Label>
                <select value={e.menu_desglose.momento || "Desayuno"} onChange={(ev) => setMenu({ momento: ev.target.value })} className="border border-stone-300 rounded-md px-2 py-2 text-sm w-full" data-testid="sel-menu-momento">
                  {["Desayuno", "Brunch", "Coffee Break", "Comida", "Cena", "Cocktail"].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Coffee Break (uno por línea)</Label>
              <Textarea
                value={(e.menu_desglose.coffee_break || []).join("\n")}
                onChange={(ev) => setMenu({ coffee_break: ev.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                rows={4}
                placeholder="Galletas surtidas&#10;Pan dulce&#10;Donitas con canela"
                data-testid="ta-menu-coffee"
              />
            </div>
            <div>
              <Label>Platillos (formato: <code className="text-xs">Nombre :: Descripción :: opción1|opción2</code>, uno por línea)</Label>
              <Textarea
                value={(e.menu_desglose.platillos || []).map(p =>
                  typeof p === "string"
                    ? p
                    : `${p.nombre || ""} :: ${p.descripcion || ""} :: ${(p.opciones || []).join("|")}`
                ).join("\n")}
                onChange={(ev) => {
                  const parsed = ev.target.value.split("\n").filter(l => l.trim()).map(line => {
                    const [nombre, descripcion, opts] = line.split("::").map(s => (s || "").trim());
                    return { nombre, descripcion, opciones: opts ? opts.split("|").map(s => s.trim()).filter(Boolean) : [] };
                  });
                  setMenu({ platillos: parsed });
                }}
                rows={5}
                placeholder="Chilaquiles con pollo :: Elige tu salsa favorita :: Salsa verde|Salsa roja|Salsa de mole&#10;Omelette :: Preparado al momento con ingredientes frescos ::&#10;Mollete :: Pan dorado con frijoles y queso ::"
                data-testid="ta-menu-platillos"
              />
            </div>
            <div>
              <Label>Bebidas (una por línea)</Label>
              <Textarea
                value={(e.menu_desglose.bebidas || []).map(b => typeof b === "string" ? b : b.nombre || "").join("\n")}
                onChange={(ev) => setMenu({ bebidas: ev.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                rows={3}
                placeholder="Café americano&#10;Naranjada&#10;Agua de jamaica"
                data-testid="ta-menu-bebidas"
              />
            </div>
          </CardContent>
        </Card>

        {/* Datos bancarios · Nivel EMPRESA (reutilizable) */}
        <Card className="border-amber-300 bg-amber-50/40">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-amber-900">🏦 Datos bancarios de la EMPRESA (una sola vez)</p>
                <p className="text-xs text-amber-800">Se guardan a nivel empresa y se precargan automáticamente en todos los eventos futuros.</p>
              </div>
              <Button size="sm" onClick={guardarDatosEmpresa} disabled={savingBank} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="btn-guardar-bank-empresa">
                {savingBank ? "Guardando..." : "Guardar en empresa"}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Banco</Label>
                <Input value={empresaBank.banco} onChange={(ev) => setEmpresaBank(b => ({ ...b, banco: ev.target.value }))} placeholder="Nu México" data-testid="input-bank-emp-banco" />
              </div>
              <div>
                <Label>CLABE</Label>
                <Input value={empresaBank.clabe} onChange={(ev) => setEmpresaBank(b => ({ ...b, clabe: ev.target.value }))} placeholder="638 180 000 118294197" data-testid="input-bank-emp-clabe" />
              </div>
              <div>
                <Label>Titular</Label>
                <Input value={empresaBank.titular} onChange={(ev) => setEmpresaBank(b => ({ ...b, titular: ev.target.value }))} placeholder="Liliana Peralta" data-testid="input-bank-emp-titular" />
              </div>
              <div>
                <Label>WhatsApp de contacto</Label>
                <Input value={empresaBank.whatsapp} onChange={(ev) => setEmpresaBank(b => ({ ...b, whatsapp: ev.target.value }))} placeholder="999 338 8933" data-testid="input-bank-emp-whatsapp" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datos bancarios · ESTE EVENTO (opcional, override) */}
        <Card className="border-stone-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-semibold text-stone-700">Datos bancarios de ESTE evento (opcional · sobreescribe empresa)</p>
              <Button size="sm" variant="outline" onClick={() => updEvento({ datos_bancarios: { ...empresaBank } })} data-testid="btn-copiar-bank-empresa">
                Copiar de empresa
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Banco</Label>
                <Input value={e.datos_bancarios.banco || ""} onChange={(ev) => updEvento({ datos_bancarios: { ...e.datos_bancarios, banco: ev.target.value } })} placeholder="Nu México" data-testid="input-banco" />
              </div>
              <div>
                <Label>CLABE</Label>
                <Input value={e.datos_bancarios.clabe || ""} onChange={(ev) => updEvento({ datos_bancarios: { ...e.datos_bancarios, clabe: ev.target.value } })} placeholder="638 180 000 118294197" data-testid="input-clabe" />
              </div>
              <div>
                <Label>Titular</Label>
                <Input value={e.datos_bancarios.titular || ""} onChange={(ev) => updEvento({ datos_bancarios: { ...e.datos_bancarios, titular: ev.target.value } })} placeholder="Liliana Peralta" data-testid="input-titular" />
              </div>
              <div>
                <Label>WhatsApp de contacto</Label>
                <Input value={e.datos_bancarios.whatsapp || ""} onChange={(ev) => updEvento({ datos_bancarios: { ...e.datos_bancarios, whatsapp: ev.target.value } })} placeholder="999 338 8933" data-testid="input-whatsapp-empresa" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de vista previa */}
      {previewTipo && (
        <div className="fixed inset-0 bg-black/60 z-50 flex flex-col p-2 sm:p-6" data-testid="preview-modal">
          <div className="bg-white rounded-lg flex-1 flex flex-col overflow-hidden max-w-5xl mx-auto w-full">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
              <div>
                <h3 className="font-display text-lg text-stone-900">Vista previa · {TIPOS_DOC.find(t => t.key === previewTipo)?.label}</h3>
                <p className="text-xs text-stone-500">Usa &quot;Imprimir&quot; y elige &quot;Guardar como PDF&quot; en tu navegador.</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={imprimirDoc} data-testid="btn-print"><Printer className="w-4 h-4 mr-1" />Imprimir / PDF</Button>
                <Button size="sm" variant="outline" onClick={() => abrirEnNuevaVentana(previewTipo)} data-testid="btn-open-new"><Download className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => { setPreviewTipo(null); setPreviewHtml(""); }} data-testid="btn-close-preview">Cerrar</Button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-stone-100">
              {previewHtml ? (
                <iframe
                  id="doc-preview-iframe"
                  srcDoc={previewHtml}
                  title="preview"
                  className="w-full h-full border-0"
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Iter152 · Editor visual de copia editable (por tipo canónico) */}
      {editorTipo && (
        <div className="fixed inset-0 bg-black/60 z-50 flex flex-col p-2 sm:p-6" data-testid="editor-modal">
          <div className="bg-white rounded-lg flex-1 flex flex-col overflow-hidden max-w-5xl mx-auto w-full">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 gap-2 flex-wrap">
              <div>
                <h3 className="font-display text-lg text-stone-900">
                  Editar copia · {TIPOS_DOC.find(t => t.key === editorTipo)?.label}
                </h3>
                <p className="text-xs text-stone-500">
                  Haz clic dentro del documento para editar cualquier texto. El evento original permanece intacto.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={guardarEditor}
                  disabled={editorSaving}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="btn-editor-guardar"
                >
                  <Save className="w-4 h-4 mr-1" />{editorSaving ? "Guardando…" : "Guardar"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setEditorTipo(null); setEditorHtml(""); }}
                  data-testid="btn-editor-cerrar"
                >
                  Cerrar
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-stone-100">
              {editorHtml ? (
                <iframe
                  id="doc-editor-iframe"
                  srcDoc={editorHtml}
                  title="editor"
                  onLoad={activarEdicionVisual}
                  className="w-full h-full border-0 bg-white"
                  data-testid="editor-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosEvento;
